import { useEffect, useState } from "react";
import type { AuthResponse } from "@stellex-tycoon/shared-types";
import { useAuthStore } from "../store/auth.store";
import { authWithTelegram } from "../api/auth.api";
import { apiClient } from "../api/client";
import { acceptReferral } from "../api/referrals.api";

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  initData: string;
  initDataUnsafe?: {
    start_param?: string;
    user?: { id: number };
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

/**
 * Pull a referral code out of either:
 *   - Telegram's start_param (set when the Mini App is opened from a
 *     `t.me/<bot>/<app>?startapp=<code>` invite link), or
 *   - the URL's ?ref= query (fallback for users who paste the link into
 *     a browser instead of opening it inside Telegram).
 *
 * Caching note: we used to skip codes already in localStorage, which
 * silently broke retries when the first attempt happened before auth
 * completed. Now we always return any code present and rely on the
 * SERVER's idempotency — calling /referrals/accept with an
 * already-applied code returns {accepted:false, reason:"already_referred"}
 * cheaply. Worst case: one harmless extra request per page load.
 */
function pickPendingReferralCode(tg: TelegramWebApp | undefined): string | null {
  const fromTelegram = tg?.initDataUnsafe?.start_param?.trim() || null;
  const fromUrl = new URLSearchParams(window.location.search).get("ref")?.trim() || null;
  return fromTelegram || fromUrl;
}

export type TelegramAuthState =
  | { status: "loading" }
  | { status: "no-telegram"; reason: string; canDevLogin: boolean }
  | { status: "authenticated" }
  | { status: "error"; message: string };

const isDev = import.meta.env.DEV;

/**
 * Reads Telegram WebApp.initData on mount and exchanges for a JWT.
 * In Vite dev mode, exposes a dev-login fallback when not opened from Telegram.
 */
export function useTelegramAuth(): TelegramAuthState & {
  devLogin?: (username: string) => Promise<void>;
} {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const [state, setState] = useState<TelegramAuthState>({ status: "loading" });

  // Auth effect: exchange initData for a JWT (or skip if we already have one).
  useEffect(() => {
    if (accessToken) {
      setState({ status: "authenticated" });
      return;
    }

    const tg = window.Telegram?.WebApp;
    if (!tg || !tg.initData) {
      setState({
        status: "no-telegram",
        reason: tg ? "No initData from Telegram" : "Open from Telegram or use dev login",
        canDevLogin: isDev,
      });
      return;
    }

    tg.ready();
    tg.expand();

    authWithTelegram(tg.initData)
      .then((auth) => {
        setSession({
          accessToken: auth.accessToken,
          refreshToken: auth.refreshToken,
          user: auth.user,
        });
        setState({ status: "authenticated" });
      })
      .catch((err) => {
        const message = err?.response?.data?.message ?? err?.message ?? "auth failed";
        setState({ status: "error", message });
      });
  }, [accessToken, setSession]);

  // Referral effect: independent of auth source. Runs whenever the page
  // loads with an authenticated session AND a pending start_param/ref code.
  // Critical: returning users (who already have a token in localStorage)
  // would otherwise short-circuit through the auth effect's early-return
  // and never apply a fresh invite link's code.
  useEffect(() => {
    if (state.status !== "authenticated") return;
    const tg = window.Telegram?.WebApp;
    const refCode = pickPendingReferralCode(tg);
    if (!refCode) return;
    void acceptReferral(refCode).catch(() => {
      // Silently drop — invalid code, self-referral, already referred,
      // not auth'd yet (rare race), etc. The server-side log is the
      // authoritative trail.
    });
  }, [state.status]);

  const devLogin = async (username: string) => {
    try {
      const { data } = await apiClient.post<AuthResponse>("/auth/dev-login", { username });
      setSession({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      });
      setState({ status: "authenticated" });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err as Error).message;
      setState({ status: "error", message: msg ?? "Dev login failed" });
    }
  };

  return { ...state, devLogin };
}
